<template>
  <v-card variant="outlined" class="pa-4 pa-md-6">
    <div class="analysis-progress-columns">
      <aside class="analysis-progress-steps">
        <v-list lines="two" class="bg-transparent pa-0">
          <v-list-item
            v-for="(task, i) in normalizedTasks"
            :key="i"
            :class="{ 'active-task': task.status === 'in-progress' || task.status === 'action-required' }"
          >
            <template v-slot:prepend>
              <v-avatar :color="getTaskColor(task.status)" size="40">
                <v-icon
                  color="white"
                  :class="{ 'spinning': task.status === 'in-progress' && !isConfirmationStage }"
                >
                  {{ getTaskIcon(task.status) }}
                </v-icon>
              </v-avatar>
            </template>
            <v-list-item-title>{{ task.label }}</v-list-item-title>
            <v-list-item-subtitle v-if="task.status === 'in-progress' || task.status === 'action-required'">
              {{ task.message }}
            </v-list-item-subtitle>
            <template v-slot:append>
              <v-chip :color="getTaskColor(task.status)" variant="flat" size="small">
                {{ getTaskStatusLabel(task.status) }}
              </v-chip>
            </template>
          </v-list-item>
        </v-list>
      </aside>

      <div class="analysis-progress-main" :key="stageRenderKey">
        <template v-if="isConfirmationStage">
          <div class="text-center mb-8">
            <v-icon
              color="warning"
              size="64"
              class="mb-4"
            >
              mdi-alert-circle
            </v-icon>
            <div class="text-body-1 text-medium-emphasis">
              Action required: Please review and confirm the generated content below.
            </div>
          </div>
        </template>
        <div
          v-else-if="analysisReady"
          class="analysis-progress-ready"
        >
          <div class="text-h5 font-weight-bold mb-2">See how AI sees your brand</div>
          <div class="text-body-1 text-medium-emphasis mb-8">
            Your analysis setup is complete — continue to unlock the full story.
          </div>
          <v-btn
            size="large"
            class="brand-upgrade-cta-btn analysis-continue-btn text-none px-8"
            @click="$emit('continue')"
          >
            Continue
            <v-icon end>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
        <div
          v-else-if="!hasFailed"
          class="analysis-progress-loading-cluster"
        >
          <div class="text-center">
            <v-icon
              color="primary"
              size="64"
              class="mb-4 spinning-icon"
            >
              mdi-cog
            </v-icon>
            <div class="text-body-1 text-medium-emphasis">
              Analyzing your brand and generating insights...
            </div>
          </div>
          <!-- <v-progress-linear
            v-if="progress < 100"
            :model-value="progress"
            :height="8"
            color="primary"
            class="mt-6 loading-progress-bar"
            rounded
          ></v-progress-linear> -->
        </div>

    <!-- Confirmation UI for Competitors -->
    <div v-if="showCompetitorReview" class="confirmation-section">
      <div class="text-center mb-6">
        <v-icon color="primary" size="64" class="mb-4">mdi-pencil-outline</v-icon>
        <div class="text-h5 mb-2">Review Suggested Competitors</div>
        <div class="text-body-1 text-medium-emphasis">
          These competitors were the most frequently mentioned brands in your Gemini query responses. Edit the list if needed, then click Continue.
        </div>
      </div>

      <v-alert v-if="loadingSuggestedCompetitors" type="info" variant="tonal" class="mb-6">
        Loading competitor suggestions from query results...
      </v-alert>

      <v-alert v-if="suggestedCompetitorsError" type="error" variant="tonal" class="mb-6">
        {{ suggestedCompetitorsError }}
      </v-alert>

      <v-alert
        v-if="!loadingSuggestedCompetitors && !suggestedCompetitorsError && !hasSuggestedCompetitors"
        type="warning"
        variant="tonal"
        class="mb-6"
      >
        No competitors were found in your query responses. Add competitors manually below to continue.
      </v-alert>

      <div v-if="!loadingSuggestedCompetitors && editingCompetitors.products?.length" class="mb-6">
        <v-card variant="outlined" class="pa-4 mb-4">
          <div class="text-h6 font-weight-bold mb-4 pb-2" style="border-bottom: 2px solid #e0e0e0;">
            Primary Product
          </div>
          <v-text-field
            v-model="editingCompetitors.products[0].name"
            label="Product Name"
            variant="outlined"
            density="comfortable"
            :disabled="true"
          ></v-text-field>
        </v-card>

        <v-card variant="outlined" class="pa-4">
          <div class="text-h6 font-weight-bold mb-4 pb-2" style="border-bottom: 2px solid #e0e0e0;">
            Competitors
          </div>
          <template v-for="(competitor, index) in editingCompetitors.products" :key="index">
            <div
              v-if="index > 0"
              class="mb-4"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="text-subtitle-2 font-weight-medium text-primary">
                  Competitor #{{ index }}
                </div>
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  color="error"
                  @click="removeCompetitor(index)"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
              <v-text-field
                v-model="competitor.name"
                label="Competitor Name"
                variant="outlined"
                density="comfortable"
              ></v-text-field>
            </div>
          </template>
          
          <!-- Add new competitor button -->
          <v-btn
            color="primary"
            variant="outlined"
            size="small"
            @click="addCompetitor"
            class="mt-2"
          >
            <v-icon class="mr-2">mdi-plus</v-icon>
            Add Competitor
          </v-btn>
        </v-card>
      </div>

      <div class="d-flex justify-center mt-6">
        <v-btn
          color="primary"
          size="large"
          :loading="savingCompetitors"
          :disabled="loadingSuggestedCompetitors || !editingCompetitors.products?.length"
          @click="confirmCompetitors"
        >
          Continue
          <v-icon class="ml-2">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Confirmation UI for Personas -->
    <div v-else-if="currentProcessingStage === 'confirm_personas'" class="confirmation-section">
      <div class="text-center mb-6">
        <v-icon color="primary" size="64" class="mb-4">mdi-pencil-outline</v-icon>
        <div class="text-h5 mb-2">Review Generated Personas</div>
        <div class="text-body-1 text-medium-emphasis">
          Please review and edit the AI-generated personas below, then click Continue to proceed.
        </div>
      </div>

      <v-alert v-if="!editingPersonas || editingPersonas.length === 0" type="info" variant="tonal" class="mb-6">
        No personas available yet. Please wait for persona generation to complete.
      </v-alert>

      <div v-else>
        <div class="d-flex justify-end mb-4" style="gap: 12px;">
          <v-btn
            variant="text"
            size="small"
            class="text-none"
            :disabled="expandedPersonaPanels.length === editingPersonas.length"
            @click="expandAllPersonas"
          >
            Expand All
          </v-btn>
          <v-btn
            variant="text"
            size="small"
            class="text-none"
            :disabled="expandedPersonaPanels.length === 0"
            @click="collapseAllPersonas"
          >
            Collapse All
          </v-btn>
        </div>

        <v-expansion-panels
          v-model="expandedPersonaPanels"
          multiple
          variant="accordion"
          class="mb-6"
        >
          <v-expansion-panel
            v-for="(persona, index) in editingPersonas"
            :key="index"
          >
          <v-expansion-panel-title>
            <div class="d-flex align-center justify-space-between w-100 pr-4">
              <div>
                <div class="text-subtitle-1 font-weight-medium">
                  {{ persona.name || `Persona ${index + 1}` }}
                </div>
                <div v-if="persona.motivation" class="text-caption text-medium-emphasis">
                  {{ persona.motivation.substring(0, 60) }}...
                </div>
              </div>
              <v-btn
                variant="text"
                size="small"
                class="text-none"
                @click.stop="togglePersonaPanel(index)"
              >
                {{ isPersonaPanelExpanded(index) ? 'Click to collapse' : 'Click to expand' }}
              </v-btn>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-divider class="mb-4"></v-divider>
            
            <!-- Editable Persona Fields -->
            <v-text-field
              v-model="persona.name"
              label="Persona Name"
              variant="outlined"
              class="mb-4"
            ></v-text-field>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="persona.demographic.age_range"
                  label="Age Range"
                  variant="outlined"
                  class="mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="persona.demographic.location"
                  label="Location"
                  variant="outlined"
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="persona.demographic.income_bracket"
                  label="Income Bracket"
                  variant="outlined"
                  class="mb-4"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="persona.demographic.gender_focus"
                  label="Gender Focus"
                  variant="outlined"
                  class="mb-4"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-textarea
              v-model="persona.motivation"
              label="Purchase Motivation"
              variant="outlined"
              rows="3"
              auto-grow
              class="mb-4"
            ></v-textarea>

            <v-textarea
              v-model="persona.product_focus"
              label="Product Focus"
              variant="outlined"
              rows="2"
              auto-grow
              class="mb-4"
            ></v-textarea>
          </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <div class="d-flex justify-center mt-6">
        <v-btn
          color="primary"
          size="large"
          :loading="savingPersonas"
          @click="confirmPersonas"
        >
          Continue
          <v-icon class="ml-2">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Confirmation UI for Queries -->
    <div v-else-if="currentProcessingStage === 'confirm_queries'" class="confirmation-section">
      <div class="text-center mb-6">
        <v-icon color="primary" size="64" class="mb-4">mdi-pencil-outline</v-icon>
        <div class="text-h5 mb-2">Review Generated Queries</div>
        <div class="text-body-1 text-medium-emphasis">
          Please review and edit the AI-generated queries below, then click Continue to proceed.
        </div>
      </div>

      <v-alert v-if="!editingQueries || editingQueries.length === 0" type="info" variant="tonal" class="mb-6">
        No queries available yet. Please wait for query generation to complete.
      </v-alert>

      <div v-else class="mb-6">
        <v-card
          v-for="personaGroup in queriesByPersona"
          :key="personaGroup.personaId"
          variant="outlined"
          class="mb-4 pa-4"
        >
          <!-- Persona Name Header -->
          <div class="text-h6 font-weight-bold mb-4 pb-2" style="border-bottom: 2px solid #e0e0e0;">
            {{ personaGroup.personaName }}
          </div>

          <!-- Queries for this Persona -->
          <div
            v-for="(query, index) in personaGroup.queries"
            :key="query.id || index"
            class="mb-4"
          >
            <div class="text-subtitle-2 font-weight-medium mb-2 text-primary">
              #{{ index + 1 }}
            </div>
            <v-text-field
              v-model="query.query_text"
              label="Query Text"
              variant="outlined"
              density="comfortable"
            ></v-text-field>
          </div>
        </v-card>
      </div>

      <div class="d-flex justify-center mt-6" style="gap: 16px;">
        <v-btn
          color="primary"
          variant="outlined"
          size="large"
          @click="editPersonas"
        >
          <v-icon class="mr-2">mdi-arrow-left</v-icon>
          Edit Personas
        </v-btn>
        <v-btn
          color="primary"
          size="large"
          :loading="savingQueries"
          @click="confirmQueries"
        >
          Continue
          <v-icon class="ml-2">mdi-arrow-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Status Footer (failure only; progress lives in loading cluster) -->
    <div v-if="hasFailed" class="mt-8 text-center">
      <div class="mb-6">
        <v-alert type="error" variant="tonal" class="mb-4">
          <v-alert-title>Analysis Failed</v-alert-title>
          The AEO analysis encountered an error. This is usually due to API rate limits. Please try again in a few minutes.
        </v-alert>
        <div class="d-flex gap-2 justify-center">
          <v-btn
            v-if="showBackButton && !isConfirmationStage"
            color="primary"
            variant="outlined"
            @click="$emit('go-back')"
          >
            <v-icon class="mr-2">mdi-arrow-left</v-icon>
            Go Back
          </v-btn>
          <v-btn
            color="primary"
            @click="$emit('view-details')"
          >
            View Details
            <v-icon class="ml-2">mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </div>
    </div>
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import axios from '@/axios.js'

const BASE_URL = process.env.VUE_APP_API_BASE_URL

const props = defineProps({
  tasks: {
    type: Array,
    required: true
  },
  progress: {
    type: Number,
    default: 0
  },
  showBackButton: {
    type: Boolean,
    default: true
  },
  reviewEntityId: {
    type: Number,
    default: null
  },
  currentProcessingStage: {
    type: String,
    default: null
  },
  personas: {
    type: Array,
    default: () => []
  },
  queries: {
    type: Array,
    default: () => []
  },
  personaProfiles: {
    type: Array,
    default: () => []
  },
  competitors: {
    type: Object,
    default: () => null
  },
  needsCompetitorReview: {
    type: Boolean,
    default: false
  },
  /** Analysis finished — show completed steps + Continue CTA instead of spinner. */
  analysisReady: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['go-back', 'view-details', 'continue', 'competitors-confirmed', 'personas-confirmed', 'queries-confirmed', 'edit-personas'])

const editingPersonas = ref([])
const editingQueries = ref([])
const editingCompetitors = ref({ products: [] })
const expandedPersonaPanels = ref([])
const savingPersonas = ref(false)
const savingQueries = ref(false)
const savingCompetitors = ref(false)
const loadingSuggestedCompetitors = ref(false)
const suggestedCompetitorsError = ref(null)
const hasSuggestedCompetitors = ref(false)

const showCompetitorReview = computed(() => {
  return props.needsCompetitorReview || props.currentProcessingStage === 'confirm_competitors'
})

const resetPersonaExpansionToFirst = () => {
  expandedPersonaPanels.value = editingPersonas.value && editingPersonas.value.length > 0 ? [0] : []
}

const isPersonaPanelExpanded = (index) => {
  return expandedPersonaPanels.value.includes(index)
}

const togglePersonaPanel = (index) => {
  if (isPersonaPanelExpanded(index)) {
    expandedPersonaPanels.value = expandedPersonaPanels.value.filter(i => i !== index)
  } else {
    expandedPersonaPanels.value = [...expandedPersonaPanels.value, index]
  }
}

const expandAllPersonas = () => {
  expandedPersonaPanels.value = (editingPersonas.value || []).map((_, idx) => idx)
}

const collapseAllPersonas = () => {
  expandedPersonaPanels.value = []
}

// Initialize editing data when props change
// Use deep copy to avoid mutating props when editing nested objects
watch(() => props.personas, (newPersonas) => {
  const hasPersonas = newPersonas && newPersonas.length > 0

  if (!hasPersonas) {
    editingPersonas.value = []
    expandedPersonaPanels.value = []
    return
  }

  // Reload personas only on initial load or when regenerated (IDs changed).
  const shouldReload = editingPersonas.value.length === 0 ||
                        (editingPersonas.value.length > 0 && newPersonas[0]?.id !== editingPersonas.value[0]?.id)

  if (shouldReload) {
    editingPersonas.value = JSON.parse(JSON.stringify(newPersonas))

    if (props.currentProcessingStage === 'confirm_personas') {
      resetPersonaExpansionToFirst()
    } else {
      expandedPersonaPanels.value = []
    }
  }
}, { immediate: true })

const loadSuggestedCompetitors = async () => {
  if (!props.reviewEntityId) return

  loadingSuggestedCompetitors.value = true
  suggestedCompetitorsError.value = null

  try {
    const response = await axios.get(
      `${BASE_URL}/review_entities/${props.reviewEntityId}/suggested_competitors`,
      {
        headers: { Authorization: localStorage.getItem('auth_token') }
      }
    )

    if (response.data?.success && response.data.competitors?.products) {
      editingCompetitors.value = JSON.parse(JSON.stringify(response.data.competitors))
      hasSuggestedCompetitors.value = response.data.has_suggestions === true
    } else {
      suggestedCompetitorsError.value = response.data?.error || 'Failed to load competitor suggestions.'
    }
  } catch (error) {
    console.error('Failed to load suggested competitors:', error)
    suggestedCompetitorsError.value = error.response?.data?.error || 'Failed to load competitor suggestions.'
  } finally {
    loadingSuggestedCompetitors.value = false
  }
}

watch(() => props.currentProcessingStage, (newStage) => {
  if (newStage === 'confirm_personas') {
    resetPersonaExpansionToFirst()
  } else {
    expandedPersonaPanels.value = []
  }

  if (newStage === 'confirm_competitors' || props.needsCompetitorReview) {
    loadSuggestedCompetitors()
  }
}, { immediate: true })

watch(() => props.needsCompetitorReview, (needsReview) => {
  if (needsReview) {
    loadSuggestedCompetitors()
  }
})

watch(() => props.queries, (newQueries) => {
  // Reload queries only on initial load or when regenerated (IDs changed).
  if (newQueries && newQueries.length > 0) {
    const shouldReload = editingQueries.value.length === 0 || 
                        (editingQueries.value.length > 0 && newQueries[0]?.id !== editingQueries.value[0]?.id)
    
    if (shouldReload) {
      editingQueries.value = JSON.parse(JSON.stringify(newQueries))
    }
  }
}, { immediate: true })

watch(() => props.competitors, (newCompetitors) => {
  // Reload competitors from parent when provided (e.g. after save); on-demand fetch handles initial load.
  if (newCompetitors && newCompetitors.products && editingCompetitors.value.products.length === 0) {
    editingCompetitors.value = JSON.parse(JSON.stringify(newCompetitors))
  }
}, { immediate: true })

const getPersonaName = (personaId) => {
  if (!personaId || !props.personaProfiles || props.personaProfiles.length === 0) return 'Unknown'
  // Find persona by id - personas now include id field from API
  const persona = props.personaProfiles.find(p => p.id === personaId)
  if (persona) {
    return persona.name || `Persona ${personaId}`
  }
  return `Persona ${personaId}`
}

// Group queries by persona
const queriesByPersona = computed(() => {
  if (!editingQueries.value || editingQueries.value.length === 0) {
    return []
  }

  // Group queries by persona_id
  const grouped = {}
  
  editingQueries.value.forEach((query) => {
    const personaId = query.persona_id
    if (!grouped[personaId]) {
      grouped[personaId] = {
        personaId: personaId,
        personaName: getPersonaName(personaId),
        queries: []
      }
    }
    grouped[personaId].queries.push(query)
  })

  // Convert to array and ensure personas are in the same order as personaProfiles
  const result = []
  if (props.personaProfiles && props.personaProfiles.length > 0) {
    props.personaProfiles.forEach((persona) => {
      if (grouped[persona.id]) {
        result.push(grouped[persona.id])
      }
    })
  } else {
    // Fallback: just return grouped queries in any order
    result.push(...Object.values(grouped))
  }

  return result
})

const editPersonas = async () => {
  if (!props.reviewEntityId) {
    console.error('No review entity ID provided')
    return
  }

  try {
    // Update processing stage back to confirm_personas
    await axios.patch(
      `${BASE_URL}/review_entities/${props.reviewEntityId}/update_processing_stage`,
      { processing_stage: 'confirm_personas' },
      {
        headers: { Authorization: localStorage.getItem('auth_token') }
      }
    )

    emit('edit-personas')
  } catch (error) {
    console.error('Failed to go back to persona editing:', error)
    alert(error.response?.data?.error || 'Failed to go back to persona editing. Please try again.')
  }
}

const confirmPersonas = async () => {
  if (!props.reviewEntityId) {
    console.error('No review entity ID provided')
    return
  }

  savingPersonas.value = true
  try {
    // First update personas
    await axios.patch(
      `${BASE_URL}/review_entities/${props.reviewEntityId}/update_personas`,
      { personas: editingPersonas.value },
      {
        headers: { Authorization: localStorage.getItem('auth_token') }
      }
    )

    // Then confirm them
    await axios.post(
      `${BASE_URL}/review_entities/${props.reviewEntityId}/confirm_personas`,
      {},
      {
        headers: { Authorization: localStorage.getItem('auth_token') }
      }
    )

    emit('personas-confirmed')
  } catch (error) {
    console.error('Failed to confirm personas:', error)
    alert(error.response?.data?.error || 'Failed to confirm personas. Please try again.')
  } finally {
    savingPersonas.value = false
  }
}

const confirmQueries = async () => {
  if (!props.reviewEntityId) {
    console.error('No review entity ID provided')
    return
  }

  savingQueries.value = true
  try {
    // First update queries
    await axios.patch(
      `${BASE_URL}/review_entities/${props.reviewEntityId}/update_queries`,
      { queries: editingQueries.value },
      {
        headers: { Authorization: localStorage.getItem('auth_token') }
      }
    )

    // Then confirm them
    await axios.post(
      `${BASE_URL}/review_entities/${props.reviewEntityId}/confirm_queries`,
      {},
      {
        headers: { Authorization: localStorage.getItem('auth_token') }
      }
    )

    emit('queries-confirmed')
  } catch (error) {
    console.error('Failed to confirm queries:', error)
    alert(error.response?.data?.error || 'Failed to confirm queries. Please try again.')
  } finally {
    savingQueries.value = false
  }
}

const currentTask = computed(() => {
  const inProgressTask = normalizedTasks.value.find(task => task.status === 'in-progress' || task.status === 'action-required')
  return inProgressTask || null
})

const hasFailed = computed(() => {
  return normalizedTasks.value.some(task => task.status === 'failed')
})

const isConfirmationStage = computed(() => {
  return showCompetitorReview.value || props.currentProcessingStage === 'confirm_personas' || props.currentProcessingStage === 'confirm_queries'
})

const stageRenderKey = computed(() => {
  if (props.analysisReady) return 'analysis-ready'
  return props.currentProcessingStage || 'processing'
})

const addCompetitor = () => {
  if (!editingCompetitors.value.products) {
    editingCompetitors.value.products = []
  }
  editingCompetitors.value.products.push({
    name: '',
    is_primary: false
  })
}

const removeCompetitor = (index) => {
  if (!editingCompetitors.value.products || editingCompetitors.value.products.length <= 1) {
    return // Don't remove the primary product
  }
  editingCompetitors.value.products.splice(index, 1)
}

const confirmCompetitors = async () => {
  if (!props.reviewEntityId) {
    console.error('No review entity ID provided')
    return
  }

  savingCompetitors.value = true
  try {
    // First update competitors
    await axios.patch(
      `${BASE_URL}/review_entities/${props.reviewEntityId}/update_competitors`,
      { competitors: editingCompetitors.value },
      {
        headers: { Authorization: localStorage.getItem('auth_token') }
      }
    )

    // Then confirm them
    await axios.post(
      `${BASE_URL}/review_entities/${props.reviewEntityId}/confirm_competitors`,
      {},
      {
        headers: { Authorization: localStorage.getItem('auth_token') }
      }
    )

    emit('competitors-confirmed')
  } catch (error) {
    console.error('Failed to confirm competitors:', error)
    alert(error.response?.data?.error || 'Failed to confirm competitors. Please try again.')
  } finally {
    savingCompetitors.value = false
  }
}

const getTaskColor = (status) => {
  switch (status) {
    case 'completed':
      return 'success'
    case 'in-progress':
      return 'primary'
    case 'action-required':
      return 'warning'
    case 'failed':
      return 'error'
    default:
      return 'grey'
  }
}

const getTaskIcon = (status) => {
  switch (status) {
    case 'completed':
      return 'mdi-check-circle'
    case 'in-progress':
      return 'mdi-loading'
    case 'action-required':
      return 'mdi-alert-circle'
    case 'failed':
      return 'mdi-alert-circle'
    default:
      return 'mdi-clock-outline'
  }
}

const getTaskStatusLabel = (status) => {
  switch (status) {
    case 'completed':
      return '✓ Complete'
    case 'in-progress':
      return 'In Progress'
    case 'action-required':
      return '⚠ Action Required'
    case 'failed':
      return '✗ Failed'
    default:
      return '⏸ Pending'
  }
}

// Keep parent-provided task states as the source of truth.
// Parallel jobs can legitimately leave earlier tasks incomplete while later tasks start.
const normalizedTasks = computed(() => {
  if (!props.tasks || props.tasks.length === 0) {
    return []
  }
  if (props.analysisReady) {
    return props.tasks.map((task) => ({
      ...task,
      status: 'completed',
      message: ''
    }))
  }
  return props.tasks.map((task) => ({ ...task }))
})
</script>

<style scoped>
.analysis-progress-columns {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 7fr);
  gap: 24px;
  align-items: stretch;
}

@media (max-width: 959px) {
  .analysis-progress-columns {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 960px) {
  .analysis-progress-columns {
    grid-template-columns: minmax(0, 3.2fr) minmax(0, 6.8fr);
  }
}

.analysis-progress-steps,
.analysis-progress-main {
  min-width: 0;
}

.analysis-progress-main {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.analysis-progress-loading-cluster {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  width: 100%;
  min-height: min(320px, 50vh);
}

.analysis-progress-ready {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  width: 100%;
  min-height: min(320px, 50vh);
  padding: 24px 16px;
}

.analysis-continue-btn.v-btn {
  min-height: 48px;
  padding-inline: 28px !important;
  opacity: 1;
}

.analysis-progress-loading-cluster > .text-center {
  width: 100%;
}

.active-task {
  background-color: rgba(25, 118, 210, 0.04);
  transition: background-color 0.3s ease;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spinning {
  animation: spin 1s linear infinite;
}

.spinning-icon {
  animation: spin 2s linear infinite;
}
</style>

